import { motion } from 'framer-motion';
import ISO6391 from 'iso-639-1';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Outlet, useNavigate, useParams, useResolvedPath } from 'react-router-dom';

import { useGetPersonsByIdQuery } from '@client/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DD,
  DT,
  GridCol,
  GridRow,
  isDefinedString,
  PageBreadcrumbs,
  PageDetailContent,
  PageDetailHeader,
  PageNavigation,
  Skeleton,
  Text,
  toCountry,
  useRouteDocumentTitle,
} from '@client/toolkit';
import { timeZonesNames } from '@vvo/tzdb';
import { UserDeleteModal, UserEditModal } from './components';
import { BackButton } from '../../../components';
import { UsersPaths } from '../users.router';
import { PageContentAnimation } from '../../../components';
import { PageListLayout } from '../../../components/layout/page-list-layout';

const getLanguage = (lng?: string) => (!lng ? undefined : ISO6391.getName(lng));

const getTimezone = (tz?: string) => timeZonesNames.find((name) => name === tz);

export function UserRoute() {
  const { t, ready } = useTranslation('users');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userPath = useResolvedPath(UsersPaths.User);

  useRouteDocumentTitle(t('documentTitle'), userPath.pathname);

  const { data: userData, isFetching } = useGetPersonsByIdQuery({ id: id ?? '' }, { skip: id == null });

  const [editing, setEditing] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleBack = () => {
    navigate(generatePath(UsersPaths.Users));
  };

  const handleEditClose = () => {
    setEditing(false);
  };

  const handleEditAfterSubmit = () => {
    setEditing(false);
  };

  const handleClickDeleteUser = () => {
    setDeleting(true);
  };

  const handleCloseDeleteUser = () => {
    setDeleting(false);
  };

  const handleDeleteUser = () => {
    navigate(generatePath(UsersPaths.Users));
  };

  return (
    <PageListLayout>
      <PageNavigation>
        <PageBreadcrumbs className="py-4">
          <PageBreadcrumbs.Link onClick={handleBack}>
            {ready ? t('overview') : <Skeleton variant="text" width="w-24" />}
          </PageBreadcrumbs.Link>
          <PageBreadcrumbs.Link>{ready ? t('title') : <Skeleton variant="text" width="w-24" />}</PageBreadcrumbs.Link>
        </PageBreadcrumbs>
      </PageNavigation>
      <PageDetailHeader>
        <PageContentAnimation>
          <div className="flex w-full justify-between">
            <BackButton />
            <div className="flex">
              <Button color="danger" fontSize="sm" disabled={isFetching || id == null} onClick={handleClickDeleteUser}>
                {tCommon('delete')}
              </Button>
            </div>
          </div>
        </PageContentAnimation>
      </PageDetailHeader>

      <PageDetailContent>
        <PageContentAnimation>
          <div className="w-full mb-2 space-y-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex-1" variant="dense" loading={!ready}>
                  {t('profile')}
                </CardTitle>
              </CardHeader>

              <CardContent>
                <div className="space-y-2 pb-2">
                  {/* User information */}
                  <div>
                    <Text
                      as="div"
                      loading={isFetching}
                      isEmpty={!isDefinedString(userData?.name)}
                      className="font-medium text-lg text-primary"
                    >
                      {userData?.name}
                    </Text>
                    <Text
                      as="div"
                      loading={isFetching}
                      isEmpty={!isDefinedString(userData?.name)}
                      className="mb-3 font-medium text-sm text-primary"
                    >
                      {t('userDetails.role', { role: tType(`UserRole.${userData?.role}`) })}
                    </Text>

                    <GridRow cols={3} spacingY={1} spacingX={2} className="items-center">
                      <GridCol span={1}>
                        <DT>{tGlobal('user.email')}</DT>
                      </GridCol>
                      <GridCol span={2} loading={isFetching}>
                        <DD loading={isFetching} isEmpty={!isDefinedString(userData?.email)}>
                          {userData?.email}
                        </DD>
                      </GridCol>

                      <GridCol span={1}>
                        <DT>{tGlobal('user.country')}</DT>
                      </GridCol>
                      <GridCol span={2} loading={isFetching}>
                        <DD
                          loading={isFetching}
                          isEmpty={!isDefinedString(userData?.socialSecurityNumber?.country?.threeLetterCode)}
                        >
                          {toCountry(userData?.socialSecurityNumber?.country?.threeLetterCode)?.name}
                        </DD>
                      </GridCol>

                      <GridCol span={1}>
                        <DT>{tGlobal('user.language')}</DT>
                      </GridCol>
                      <GridCol span={2} loading={isFetching}>
                        <DD loading={isFetching} isEmpty={!isDefinedString(getLanguage(userData?.culture?.language))}>
                          {getLanguage(userData?.culture?.language)}
                        </DD>
                      </GridCol>

                      <GridCol span={1}>
                        <DT>{tGlobal('user.timezone')}</DT>
                      </GridCol>
                      <GridCol span={2} loading={isFetching}>
                        <DD loading={isFetching} isEmpty={!isDefinedString(getTimezone(userData?.culture?.timezone))}>
                          {getTimezone(userData?.culture?.timezone)}
                        </DD>
                      </GridCol>
                    </GridRow>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </PageContentAnimation>
      </PageDetailContent>

      {id != null && (
        <>
          <UserEditModal
            id={id}
            open={editing}
            user={userData}
            onClose={handleEditClose}
            onAfterSubmit={handleEditAfterSubmit}
          />
          <UserDeleteModal
            user={userData ?? null}
            open={deleting}
            onCancel={handleCloseDeleteUser}
            onDelete={handleDeleteUser}
          />
        </>
      )}

      <Outlet />
    </PageListLayout>
  );
}
